import React, { useState, useEffect, useRef, useCallback } from 'react';
import KpiTile from './components/KpiTile';
import { fetchOverview } from './api';
import { useFilters } from './FilterContext';

const CARD_STYLE = {
  backgroundColor: '#fff',
  borderRadius: 5,
  border: '1px solid #d1d5db',
  padding: '10px 12px',
  boxShadow: '0 1px 2px rgba(0,0,0,.06),0 1px 3px rgba(0,0,0,.08)',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
};

const CARD_TITLE = {
  fontSize: 9,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  color: '#9ca3af',
  marginBottom: 6,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexShrink: 0,
};

const PRODUCT_PALETTE = [
  '#2563eb','#7c3aed','#059669','#0891b2','#d97706',
  '#dc2626','#ea580c','#db2777','#65a30d','#6b7280',
];

const MARKET_BADGE = {
  US: { bg: '#dbeafe', text: '#1d4ed8' },
  EU: { bg: '#d1fae5', text: '#065f46' },
  EP: { bg: '#ede9fe', text: '#5b21b6' },
};

function tatColor(avg) {
  if (avg <= 5) return '#059669';
  if (avg <= 10) return '#0891b2';
  if (avg <= 15) return '#d97706';
  return '#dc2626';
}

const INFLOW_COLORS = ['#34d399', '#60a5fa', '#fbbf24', '#f87171']; // Completed, Pending, Running, Cancelled
const INFLOW_KEYS  = ['completed', 'pending', 'running', 'cancelled'];

// Derive Monday date from "W08" week string (ISO week, current year)
function weekToDate(weekStr) {
  const weekNum = parseInt(weekStr.replace('W', ''), 10);
  if (!weekNum) return '';
  const year = new Date().getFullYear();
  const jan4 = new Date(year, 0, 4);
  const mondayW1 = new Date(jan4);
  mondayW1.setDate(jan4.getDate() - ((jan4.getDay() + 6) % 7));
  const monday = new Date(mondayW1);
  monday.setDate(mondayW1.getDate() + (weekNum - 1) * 7);
  return monday.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }); // "5 Jan"
}

function WeeklyInflowChart({ data, maxWeekTotal }) {
  const PAD = { top: 18, right: 6, bottom: 36, left: 30 };
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null); // { x, y, week, completed, pending, running, cancelled, total }

  const handleBarHover = useCallback((barData, e) => {
    setTooltip({
      x: e.clientX,
      y: e.clientY,
      ...barData,
    });
  }, []);

  const handleBarLeave = useCallback(() => setTooltip(null), []);

  if (!data || data.length === 0) {
    return <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: 11 }}>No data</div>;
  }

  // Nice round y-axis max
  const rawMax = maxWeekTotal || 1;
  const step = rawMax <= 10 ? 2 : rawMax <= 50 ? 10 : rawMax <= 200 ? 50 : rawMax <= 500 ? 100 : 200;
  const yMax = Math.ceil(rawMax / step) * step;
  const yTicks = [];
  for (let v = 0; v <= yMax; v += step) yTicks.push(v);

  return (
    <div ref={containerRef} style={{ flex: 1, minHeight: 0, position: 'relative' }}>
      <svg width="100%" height="100%" style={{ display: 'block', overflow: 'visible' }}>
        <defs>
          <style>{`
            .wi-bar { transition: opacity 0.12s; cursor: crosshair; }
            .wi-bar:hover { opacity: 0.82; }
          `}</style>
        </defs>
        <g>
          <InnerChart
            data={data}
            yMax={yMax}
            yTicks={yTicks}
            PAD={PAD}
            onBarHover={handleBarHover}
            onBarLeave={handleBarLeave}
          />
        </g>
      </svg>

      {/* Tooltip — fixed so it escapes card's overflow:hidden */}
      {tooltip && (() => {
        const TIP_W = 160;
        const flipLeft = tooltip.x + TIP_W + 16 > window.innerWidth;
        return (
          <div style={{
            position: 'fixed',
            top: tooltip.y - 8,
            left: flipLeft ? tooltip.x - TIP_W - 8 : tooltip.x + 14,
            pointerEvents: 'none',
            background: '#1a3353',
            color: '#fff',
            borderRadius: 5,
            padding: '7px 10px',
            fontSize: 10,
            boxShadow: '0 4px 12px rgba(0,0,0,.22)',
            zIndex: 99,
            minWidth: TIP_W,
          }}>
            <div style={{ fontWeight: 700, marginBottom: 5, fontSize: 11, fontFamily: "'JetBrains Mono', monospace" }}>
              {tooltip.week}
              {tooltip.weekStart && (
                <span style={{ fontWeight: 400, color: '#94a3b8', marginLeft: 6, fontSize: 9 }}>w/c {tooltip.weekStart}</span>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {[
                { color: '#34d399', label: 'Completed', val: tooltip.completed },
                { color: '#60a5fa', label: 'Pending',   val: tooltip.pending   },
                { color: '#fbbf24', label: 'Running',   val: tooltip.running   },
                { color: '#f87171', label: 'Cancelled', val: tooltip.cancelled },
              ].map(row => (
                <div key={row.label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <div style={{ width: 7, height: 7, borderRadius: 2, backgroundColor: row.color, flexShrink: 0 }} />
                    <span style={{ color: '#cbd5e1', fontSize: 9 }}>{row.label}</span>
                  </div>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 10 }}>{row.val ?? 0}</span>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 5, paddingTop: 5, borderTop: '1px solid rgba(255,255,255,.12)', display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#94a3b8', fontSize: 9 }}>Total samples</span>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 11 }}>{tooltip.total}</span>
            </div>
            <div style={{ marginTop: 4, color: '#64748b', fontSize: 8 }}>Y-axis = sample count</div>
          </div>
        );
      })()}
    </div>
  );
}

function InnerChart({ data, yMax, yTicks, PAD, onBarHover, onBarLeave }) {
  // Use a fixed viewport and let SVG scale via viewBox
  const W = 460;
  const H = 160;
  const chartW = W - PAD.left - PAD.right;
  const chartH = H - PAD.top - PAD.bottom;

  const barGroupW = chartW / data.length;
  const barW = Math.max(Math.min(barGroupW * 0.65, 28), 6);

  const yScale = (v) => chartH - (v / yMax) * chartH;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="100%" style={{ overflow: 'visible' }}>
      {/* Y-axis grid lines + labels */}
      {yTicks.map(v => {
        const y = PAD.top + yScale(v);
        return (
          <g key={v}>
            <line
              x1={PAD.left} y1={y} x2={W - PAD.right} y2={y}
              stroke={v === 0 ? '#d1d5db' : '#f0f1f3'}
              strokeWidth={v === 0 ? 1 : 0.75}
            />
            <text
              x={PAD.left - 4} y={y + 3}
              textAnchor="end"
              fontSize={7} fill="#9ca3af"
              fontFamily="'JetBrains Mono', monospace"
            >
              {v}
            </text>
          </g>
        );
      })}

      {/* Bars */}
      {data.map((w, i) => {
        const cx = PAD.left + i * barGroupW + barGroupW / 2;
        const x = cx - barW / 2;
        const total = INFLOW_KEYS.reduce((s, k) => s + (Number(w[k]) || 0), 0);

        let stackY = chartH; // starts at bottom
        const segments = INFLOW_KEYS.map((k, ki) => {
          const val = Number(w[k]) || 0;
          const h = (val / yMax) * chartH;
          const segY = stackY - h;
          stackY -= h;
          return { val, h, segY, color: INFLOW_COLORS[ki] };
        }).filter(s => s.val > 0);

        const barTop = segments.length ? Math.min(...segments.map(s => s.segY)) : chartH;

        const barHoverData = {
          week: w.week,
          weekStart: w.weekStart || weekToDate(w.week),
          completed: Number(w.completed) || 0,
          pending:   Number(w.pending)   || 0,
          running:   Number(w.running)   || 0,
          cancelled: Number(w.cancelled) || 0,
          total,
        };

        return (
          <g
            key={i}
            className="wi-bar"
            onMouseEnter={e => onBarHover && onBarHover(barHoverData, e)}
            onMouseMove={e => onBarHover && onBarHover(barHoverData, e)}
            onMouseLeave={() => onBarLeave && onBarLeave()}
          >
            {segments.map((seg, si) => (
              <rect
                key={si}
                x={x} y={PAD.top + seg.segY}
                width={barW} height={Math.max(seg.h, 1)}
                fill={seg.color}
                rx={si === segments.length - 1 ? 2 : 0}
                ry={si === segments.length - 1 ? 2 : 0}
              />
            ))}

            {/* Total label above bar */}
            {total > 0 && (
              <text
                x={cx} y={PAD.top + barTop - 3}
                textAnchor="middle"
                fontSize={7.5} fontWeight="700"
                fill="#4b5563"
                fontFamily="'JetBrains Mono', monospace"
              >
                {total}
              </text>
            )}

            {/* Date label (primary) */}
            <text
              x={cx} y={PAD.top + chartH + 12}
              textAnchor="middle"
              fontSize={7} fontWeight="600" fill="#4b5563"
              fontFamily="'DM Sans', sans-serif"
            >
              {w.weekStart || weekToDate(w.week)}
            </text>
            {/* Week number (secondary) */}
            <text
              x={cx} y={PAD.top + chartH + 22}
              textAnchor="middle"
              fontSize={6} fill="#9ca3af"
              fontFamily="'JetBrains Mono', monospace"
            >
              {w.week}
            </text>
          </g>
        );
      })}

      {/* Y-axis line */}
      <line
        x1={PAD.left} y1={PAD.top}
        x2={PAD.left} y2={PAD.top + chartH}
        stroke="#d1d5db" strokeWidth={1}
      />
    </svg>
  );
}

export default function Overview() {
  const { buildQueryParams } = useFilters();
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchOverview(buildQueryParams())
      .then(d => { if (!cancelled) setData(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [buildQueryParams]);

  if (!data) {
    return <div style={{ padding: 24, color: '#6b7280' }}>Loading overview...</div>;
  }

  const kpi = data.kpis || data.kpi || {};
  const tatByTest = [...(data.tatByTest || [])].sort((a, b) => Number(b.avgDays) - Number(a.avgDays));
  const maxAvgDays = tatByTest.length ? Math.max(...tatByTest.map(t => Number(t.avgDays) || 0)) : 1;
  const weeklyInflow = data.weeklyInflow || [];
  const topProducts = data.topProducts || [];
  const marketSummary = data.marketSummary || [];

  const maxWeekTotal = weeklyInflow.length
    ? Math.max(...weeklyInflow.map(w => (Number(w.completed) || 0) + (Number(w.pending) || 0) + (Number(w.running) || 0) + (Number(w.cancelled) || 0)), 1)
    : 1;

  const completionPct = kpi.total ? ((kpi.completed || 0) / kpi.total * 100).toFixed(1) : '0.0';
  const avgTat = kpi.avgTat != null ? Number(kpi.avgTat).toFixed(1) : '0.0';

  return (
    <div style={{
      flex: 1,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      padding: '10px 14px 10px',
    }}>
      {/* KPI Strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 7, flexShrink: 0, marginBottom: 9 }}>
        <KpiTile label="Total" value={kpi.total || 0} accentColor="#2563eb" />
        <KpiTile label="Completed" value={kpi.completed || 0} accentColor="#059669" subLabel={<><b style={{ color: '#4b5563' }}>{completionPct}%</b> completion</>} />
        <KpiTile label="Pending" value={kpi.pending || 0} accentColor="#d97706" />
        <KpiTile label="Running" value={kpi.running || 0} accentColor="#d97706" />
        <KpiTile label="Avg TAT" value={`${avgTat}d`} accentColor="#0891b2" subLabel="turnaround" />
        <KpiTile label="Stability" value={kpi.stabilityCount || 0} accentColor="#7c3aed" subLabel="stability samples" />
        <KpiTile label="Urgent Open" value={kpi.urgentOpen || 0} accentColor="#dc2626" />
      </div>

      {/* 2x2 Grid */}
      <div style={{
        flex: 1,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gridTemplateRows: '1fr 1fr',
        gap: 9,
        overflow: 'hidden',
        minHeight: 0,
      }}>
        {/* Top-left: TAT by Test */}
        <div style={CARD_STYLE}>
          <div style={CARD_TITLE}>TAT by Test</div>
          <div style={{ height: 1, background: '#e5e7eb', marginBottom: 6, flexShrink: 0 }} />
          <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
            {tatByTest.map((t, i) => {
              const avg = Number(t.avgDays) || 0;
              const cnt = Number(t.count) || 0;
              return (
                <div key={i} style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 32px 90px 38px',
                  alignItems: 'center',
                  gap: 0,
                  padding: '4px 0',
                  borderBottom: '1px solid #f3f4f6',
                }}>
                  <span style={{ fontSize: 9, color: '#4b5563', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', paddingRight: 6 }}>
                    {t.testName}
                  </span>
                  <span style={{ fontSize: 8, color: '#9ca3af', textAlign: 'right', paddingRight: 8 }}>
                    {cnt}
                  </span>
                  <div style={{ paddingRight: 8 }}>
                    <div style={{ height: 5, backgroundColor: '#f3f4f6', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{
                        width: `${(avg / maxAvgDays) * 100}%`,
                        height: '100%',
                        backgroundColor: tatColor(avg),
                        borderRadius: 3,
                      }} />
                    </div>
                  </div>
                  <span style={{
                    fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 9,
                    color: tatColor(avg), textAlign: 'right',
                  }}>
                    {avg.toFixed(1)}d
                  </span>
                </div>
              );
            })}
            {tatByTest.length === 0 && <div style={{ color: '#9ca3af', fontSize: 11 }}>No data</div>}
          </div>
        </div>

        {/* Top-right: Weekly Sample Inflow */}
        <div style={CARD_STYLE}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, marginBottom: 5 }}>
            <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: '#9ca3af' }}>
              Weekly Sample Inflow
            </span>
            {/* Legend */}
            <div style={{ display: 'flex', gap: 8 }}>
              {[
                { color: '#34d399', label: 'Completed' },
                { color: '#60a5fa', label: 'Pending' },
                { color: '#fbbf24', label: 'Running' },
                { color: '#f87171', label: 'Cancelled' },
              ].map(item => (
                <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <div style={{ width: 8, height: 8, borderRadius: 2, backgroundColor: item.color }} />
                  <span style={{ fontSize: 7.5, color: '#6b7280', fontWeight: 500 }}>{item.label}</span>
                </div>
              ))}
            </div>
          </div>
          <div style={{ height: 1, background: '#e5e7eb', marginBottom: 0, flexShrink: 0 }} />
          <WeeklyInflowChart data={weeklyInflow} maxWeekTotal={maxWeekTotal} />
        </div>

        {/* Bottom-left: Top Products */}
        <div style={CARD_STYLE}>
          <div style={CARD_TITLE}>Products by Volume</div>
          <div style={{ height: 1, background: '#e5e7eb', marginBottom: 6, flexShrink: 0 }} />
          <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 3, minHeight: 0 }}>
            {(() => {
              const maxProd = topProducts.length ? Math.max(...topProducts.map(p => p.count)) : 1;
              return topProducts.map((p, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                }}>
                  <span style={{
                    fontSize: 9, color: '#4b5563', width: 118, flexShrink: 0,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {p.productName}
                  </span>
                  <div style={{ flex: 1, height: 5, backgroundColor: '#f3f4f6', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{
                      width: `${(p.count / maxProd) * 100}%`,
                      height: '100%',
                      backgroundColor: PRODUCT_PALETTE[i % PRODUCT_PALETTE.length],
                      borderRadius: 3,
                    }} />
                  </div>
                  <span style={{
                    fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 8,
                    color: '#4b5563', width: 22, textAlign: 'right', flexShrink: 0,
                  }}>
                    {p.count}
                  </span>
                </div>
              ));
            })()}
            {topProducts.length === 0 && <div style={{ color: '#9ca3af', fontSize: 11 }}>No data</div>}
          </div>
        </div>

        {/* Bottom-right: Market Summary */}
        <div style={CARD_STYLE}>
          <div style={CARD_TITLE}>Market Summary</div>
          <div style={{ height: 1, background: '#e5e7eb', marginBottom: 6, flexShrink: 0 }} />
          <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['Market', 'Total', 'Done', 'Pend', 'Run', 'Completion'].map(h => (
                    <th key={h} style={{
                      fontSize: 8, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3,
                      color: '#9ca3af', padding: '2px 5px', textAlign: 'left',
                      borderBottom: '1px solid #e5e7eb',
                    }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {marketSummary.map((m, i) => {
                  const pct = m.total ? ((m.completed || 0) / m.total * 100) : 0;
                  const badge = MARKET_BADGE[m.market] || { bg: '#f3f4f6', text: '#6b7280' };
                  return (
                    <tr key={i}>
                      <td style={{ padding: '3px 5px', fontSize: 9, borderBottom: '1px solid #f5f5f5' }}>
                        <span style={{
                          display: 'inline-block', padding: '1px 5px', borderRadius: 2,
                          fontSize: 8, fontWeight: 700, backgroundColor: badge.bg, color: badge.text,
                        }}>
                          {m.market}
                        </span>
                      </td>
                      <td style={{ padding: '3px 5px', fontSize: 9, borderBottom: '1px solid #f5f5f5' }}>{m.total}</td>
                      <td style={{ padding: '3px 5px', fontSize: 9, borderBottom: '1px solid #f5f5f5' }}>{m.completed}</td>
                      <td style={{ padding: '3px 5px', fontSize: 9, borderBottom: '1px solid #f5f5f5' }}>{m.pending}</td>
                      <td style={{ padding: '3px 5px', fontSize: 9, borderBottom: '1px solid #f5f5f5' }}>{m.running}</td>
                      <td style={{ padding: '3px 5px', fontSize: 9, borderBottom: '1px solid #f5f5f5' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                          <div style={{ flex: 1, height: 4, backgroundColor: '#f3f4f6', borderRadius: 2, overflow: 'hidden' }}>
                            <div style={{
                              width: `${pct}%`, height: '100%', backgroundColor: '#059669', borderRadius: 2,
                            }} />
                          </div>
                          <span style={{ fontSize: 8, fontWeight: 700, color: '#059669', width: 26, textAlign: 'right' }}>
                            {pct.toFixed(0)}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {marketSummary.length === 0 && <div style={{ color: '#9ca3af', fontSize: 11, padding: 8 }}>No data</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
